import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container,
  Paper,
  TextField,
  Button,
  Stack,
  Link,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon
} from '@mui/icons-material';
import { useFormValidation, validators } from '../utils/formValidation';
import emailjs from '@emailjs/browser';

const Contact: React.FC = () => {
  const theme = useTheme();
  const [isSending, setIsSending] = useState(false);
  
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm
  } = useFormValidation(
    {
      name: '',
      email: '',
      subject: '',
      message: ''
    },
    {
      name: validators.required,
      email: validators.email,
      subject: validators.required,
      message: validators.minLength(10)
    }
  );
  
  const onSubmit = async (formValues: typeof values) => {
    setIsSending(true);
    try {
      await emailjs.send(
        'khd_service',
        'template_q11umcl',
        {
          from_name: formValues.name,
          from_email: formValues.email,
          subject: formValues.subject,
          message: formValues.message
        },
        'Lr8VEaJcjdKxDWsNe'
      );
      alert('Thank you for your message! I\'ll get back to you soon.');
      resetForm();
    } catch (error) {
      alert('Sorry, there was a problem sending your message. Please try again later.');
      console.error('EmailJS error:', error);
    } finally {
      setIsSending(false);
    }
  };

  const isFormValid =
    values.name.trim() &&
    values.email.trim() &&
    values.subject.trim() &&
    values.message.trim().length >= 10 &&
    !errors.name &&
    !errors.email &&
    !errors.subject &&
    !errors.message;

  return (
    <Box sx={{ py: 10 }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h2" 
          textAlign="center"
          gutterBottom
          sx={{ mb: 2 }}
        >
          Contact Me
        </Typography>
        <Typography 
          variant="h6" 
          component="p" 
          textAlign="center" 
          color="text.secondary"
          sx={{ 
            maxWidth: 'md',
            mx: 'auto',
            mb: 6
          }}
        >
          Have a question or want to work together? Feel free to reach out!
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 4 }}>
          <Box>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 4,
                borderRadius: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Send me a message
              </Typography>
              
              <Box 
                component="form" 
                onSubmit={(e) => handleSubmit(e, onSubmit)}
              >
                <Stack spacing={3}>
                  <TextField
                    label="Your Name"
                    name="name"
                    required
                    fullWidth
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                  />
                  
                  <TextField
                    label="Email Address"
                    name="email"
                    type="email"
                    required
                    fullWidth
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                  
                  <TextField
                    label="Subject"
                    name="subject"
                    required
                    fullWidth
                    value={values.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.subject && !!errors.subject}
                    helperText={touched.subject && errors.subject}
                  />
                  
                  <TextField
                    label="Message"
                    name="message"
                    multiline
                    rows={5}
                    required
                    fullWidth
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.message && !!errors.message}
                    helperText={touched.message && errors.message}
                  />
                  
                  <Box>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ 
                        px: 4, 
                        py: 1.25,
                        fontSize: '1rem'
                      }}
                      disabled={!isFormValid || isSending}
                    >
                      {isSending ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Box>
          
          <Box>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 4,
                height: '100%',
                borderRadius: 2,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Contact Information
              </Typography>
              
              <Box sx={{ flexGrow: 1 }}>
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ mr: 2, fontSize: 24 }} />
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                        Email
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        Fill in the form to contact me
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <LocationIcon sx={{ mr: 2, mt: 0.5, fontSize: 24 }} />
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                        Location
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        Northern Ireland, UK
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Connect with me
                </Typography>
                
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Link 
                    href="https://github.com/KHeavyy" 
                    target="_blank" 
                    sx={{ 
                      color: 'inherit',
                      '&:hover': { opacity: 0.8 }
                    }}
                  >
                    <GitHubIcon fontSize="large" />
                  </Link>
                  <Link 
                    href="https://www.linkedin.com/in/keith-dick-7967789a/" 
                    target="_blank" 
                    sx={{ 
                      color: 'inherit',
                      '&:hover': { opacity: 0.8 }
                    }}
                  >
                    <LinkedInIcon fontSize="large" />
                  </Link>
                  <Link 
                    href="https://x.com/kheavy" 
                    target="_blank" 
                    sx={{ 
                      color: 'inherit',
                      '&:hover': { opacity: 0.8 }
                    }}
                  >
                    <TwitterIcon fontSize="large" />
                  </Link>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Contact;
